import React, { useState, useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation
} from "react-router-dom";

import ScrollToTop from "./components/ScrollToTop";

// Nav & Layout
import Navbar from "./components/Navbar";
import HomeNavbar from "./components/HomeNavbar";
import Footer from "./components/Footer";

// Pages / Components
import Home from "./components/Home";
import AboutMe from "./components/AboutMe";
import Services from "./components/Services";
import ContactPage from "./components/ContactPage";
import Portfolio from "./components/Portfolio";
import WorksPage from "./components/WorksPage";

// Album / Gallery
import AlbumView from "./components/AlbumView";
import AlbumGalleryPage from "./components/AlbumGalleryPage";

// Admin / Dashboard
import AdminPanel from "./components/AdminPanel";
import Dashboard from "./components/Dashboard";

import "./App.css";

/* ---------------- Main Layout (Public App) ---------------- */
function MainAppLayout({ cartItems }) {
  const location = useLocation();

  const isAdminRoute =
    location.pathname.startsWith("/admin") ||
    location.pathname.startsWith("/dashboard");

  const isGalleryRoute = location.pathname.startsWith("/gallery/");

  const shouldShowFooter = () => {
    if (isAdminRoute || isGalleryRoute) return false;
    if (location.pathname === "/") return false;
    return true;
  };

  return (
    <div className="App">
      <ScrollToTop />

      {/* Navbar */}
      {!isAdminRoute && !isGalleryRoute && (
        location.pathname === "/" ? (
          <HomeNavbar />
        ) : (
          <Navbar cartCount={cartItems.length} />
        )
      )}

      <main className="main-content-wrapper">
        <Routes>
          {/* Core pages */}
          <Route path="/" element={<Home />} />
          <Route path="/works" element={<WorksPage />} />
          <Route path="/portfolio" element={<Portfolio />} />

          {/* Portfolio album */}
          <Route path="/album/:albumName" element={<AlbumView />} />

          {/* Works → Album Gallery */}
          <Route
            path="/gallery/:categoryId/:albumName"
            element={<AlbumGalleryPage />}
          />

          {/* Static pages */}
          <Route path="/about" element={<AboutMe />} />
          <Route path="/services" element={<Services />} />
          <Route path="/contact" element={<ContactPage />} />

          {/* Footer links */}
          <Route path="/pricing" element={<Navigate to="/services" replace />} />
          <Route path="/privacy" element={<h1>Privacy Policy</h1>} />
          <Route path="/terms" element={<h1>Terms of Service</h1>} />

          {/* 404 */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>

      {shouldShowFooter() && <Footer />}
    </div>
  );
}

/* ---------------- App Root ---------------- */
function App() {
  const [cartItems] = useState([]);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    const checkAuth = () => {
      const token = localStorage.getItem("adminToken");
      const loginTime = localStorage.getItem("adminLoginTime");

      if (token && loginTime) {
        const now = Date.now();
        const sessionLimit = 30 * 60 * 1000;

        if (now - Number(loginTime) > sessionLimit) {
          localStorage.clear();
          setIsAuthenticated(false);
        } else {
          setIsAuthenticated(true);
        }
      } else {
        setIsAuthenticated(false);
      }
    };

    checkAuth();
    const interval = setInterval(checkAuth, 60000);
    return () => clearInterval(interval);
  }, []);

  return (
    <Router>
      <Routes>
        {/* Admin Login Route */}
        <Route
          path="/admin"
          element={
            !isAuthenticated ? (
              <AdminPanel setIsAuthenticated={setIsAuthenticated} />
            ) : (
              <Navigate to="/dashboard" replace />
            )
          }
        />

        {/* Dashboard Route */}
        <Route
          path="/dashboard"
          element={
            isAuthenticated ? (
              <Dashboard setIsAuthenticated={setIsAuthenticated} />
            ) : (
              <Navigate to="/admin" replace />
            )
          }
        />

        {/* Public App */}
        <Route path="/*" element={<MainAppLayout cartItems={cartItems} />} />
      </Routes>
    </Router>
  );
}

export default App;